import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Stack,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { toast } from "react-toastify";

const OrderConfirmationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Extract query parameters from Shopify redirect
  const checkoutToken = searchParams.get("checkout_token");
  const orderId = searchParams.get("order_id");
  const orderName = searchParams.get("order_name");

  useEffect(() => {
    // Show success toast when user arrives at confirmation page
    toast.success("Order placed successfully!");

    // Clear any cart-related data from local storage
    localStorage.removeItem("shopifyCartId");
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          {/* Success Icon */}
          <CheckCircleOutlineIcon
            sx={{ fontSize: 80, color: "success.main" }}
          />

          {/* Success Message */}
          <Typography variant="h4" component="h1" gutterBottom>
            Order Confirmed!
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Thank you for your purchase. Your order has been successfully placed
            and is being processed.
          </Typography>

          {/* Order Details (if available) */}
          {(orderId || orderName || checkoutToken) && (
            <Box
              sx={{
                width: "100%",
                bgcolor: "grey.50",
                p: 3,
                borderRadius: 2,
                textAlign: "left",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Order Details
              </Typography>
              <Stack spacing={1}>
                {orderName && (
                  <Typography variant="body2">
                    <strong>Order Number:</strong> {orderName}
                  </Typography>
                )}
                {orderId && (
                  <Typography variant="body2">
                    <strong>Order ID:</strong> {orderId}
                  </Typography>
                )}
                {checkoutToken && (
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    <strong>Checkout Token:</strong> {checkoutToken}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* Information Box */}
          <Box sx={{ width: "100%", textAlign: "left" }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              You will receive an order confirmation email shortly with your
              order details and tracking information once your items are
              shipped.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can view your order status in the{" "}
              <strong>My Purchases</strong> section.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%", mt: 3 }}
          >
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate("/browse")}
            >
              Continue Shopping
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => navigate("/my-biz/purchases")}
            >
              View My Orders
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderConfirmationPage;
